import { Link } from "react-router-dom";
import { LinkedinLogo, TwitterLogo, InstagramLogo, MapPin, Phone, EnvelopeSimple, ArrowUpRight } from "@phosphor-icons/react";
import { SITE } from "@/lib/site";
import Logo from "@/components/Logo";

const Footer = () => {
  return (
    <footer
      data-testid="site-footer"
      className="relative bg-[#1f2a44] text-white"
    >
      <div className="max-w-7xl mx-auto px-5 sm:px-8 lg:px-12 pt-20 lg:pt-28 pb-10">
        {/* Big CTA */}
        <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-8 pb-16 border-b border-white/15">
          <div>
            <p className="text-xs uppercase tracking-[0.22em] text-[#fe4c1c] font-bold">
              Ready when you are
            </p>
            <h2 className="mt-4 font-display text-4xl sm:text-5xl lg:text-6xl font-bold leading-[1.05]">
              Where innovation<br />
              <span className="text-[#fe4c1c]">never ends.</span>
            </h2>
          </div>
          <Link
            to="/contact"
            data-testid="footer-cta-contact"
            className="inline-flex items-center gap-3 px-7 py-4 rounded-full bg-[#fe4c1c] text-white font-semibold hover:bg-[#e63d0e] transition-colors"
          >
            Talk to our team <ArrowUpRight size={18} weight="bold" />
          </Link>
        </div>

        {/* Columns */}
        <div className="grid grid-cols-1 md:grid-cols-12 gap-10 py-16">
          <div className="md:col-span-4">
            <Link to="/" className="flex items-center gap-3">
              <Logo invert size="lg" />
            </Link>
            <p className="mt-5 text-sm text-white/70 leading-relaxed max-w-sm">
              A Bangalore-headquartered software product company. Building the tools business runs on — starting with VerifyPro.
            </p>
            <p className="mt-4 text-[11px] font-bold uppercase tracking-[0.28em] text-[#fe4c1c]">
              {SITE.tagline}
            </p>
            <a
              href={SITE.verifyProUrl}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="footer-verifypro"
              className="mt-6 inline-flex items-center gap-2 px-4 py-2 rounded-full bg-[#fe4c1c] text-white text-xs font-semibold hover:bg-[#e63d0e] transition-colors"
            >
              <ArrowUpRight size={14} weight="bold" /> Launch VerifyPro
            </a>
            <div className="mt-6 flex items-center gap-3">
              <a href={SITE.socials.linkedin} aria-label="LinkedIn" data-testid="footer-social-linkedin" className="h-10 w-10 inline-flex items-center justify-center rounded-full border border-white/20 hover:bg-[#fe4c1c] hover:text-[#1f2a44] hover:border-transparent transition-colors">
                <LinkedinLogo size={18} weight="bold" />
              </a>
              <a href={SITE.socials.twitter} aria-label="Twitter" data-testid="footer-social-twitter" className="h-10 w-10 inline-flex items-center justify-center rounded-full border border-white/20 hover:bg-[#fe4c1c] hover:text-[#1f2a44] hover:border-transparent transition-colors">
                <TwitterLogo size={18} weight="bold" />
              </a>
              <a href={SITE.socials.instagram} aria-label="Instagram" data-testid="footer-social-instagram" className="h-10 w-10 inline-flex items-center justify-center rounded-full border border-white/20 hover:bg-[#fe4c1c] hover:text-[#1f2a44] hover:border-transparent transition-colors">
                <InstagramLogo size={18} weight="bold" />
              </a>
            </div>
          </div>

          <div className="md:col-span-3">
            <p className="text-xs uppercase tracking-[0.22em] text-[#fe4c1c] font-bold">Explore</p>
            <ul className="mt-5 space-y-3 text-sm">
              <li><Link to="/" data-testid="footer-link-home" className="text-white/80 hover:text-[#fe4c1c]">Home</Link></li>
              <li><Link to="/about" data-testid="footer-link-about" className="text-white/80 hover:text-[#fe4c1c]">About</Link></li>
              <li><Link to="/services" data-testid="footer-link-services" className="text-white/80 hover:text-[#fe4c1c]">Products</Link></li>
              <li><Link to="/contact" data-testid="footer-link-contact" className="text-white/80 hover:text-[#fe4c1c]">Contact</Link></li>
            </ul>
          </div>

          <div className="md:col-span-3">
            <p className="text-xs uppercase tracking-[0.22em] text-[#fe4c1c] font-bold">Products</p>
            <ul className="mt-5 space-y-3 text-sm">
              <li>
                <a
                  href={SITE.verifyProUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="footer-link-verifypro"
                  className="text-white/80 hover:text-[#fe4c1c] inline-flex items-center gap-1"
                >
                  VerifyPro <ArrowUpRight size={12} weight="bold" />
                </a>
              </li>
              <li><Link to="/services#opsdesk" data-testid="footer-link-opsdesk" className="text-white/80 hover:text-[#fe4c1c]">OpsDesk <span className="text-white/40 text-[10px] uppercase tracking-wider ml-1">Soon</span></Link></li>
              <li><Link to="/services#managed-services" data-testid="footer-link-managed" className="text-white/80 hover:text-[#fe4c1c]">Managed services</Link></li>
            </ul>
          </div>

          <div className="md:col-span-2">
            <p className="text-xs uppercase tracking-[0.22em] text-[#fe4c1c] font-bold">Reach Us</p>
            <ul className="mt-5 space-y-4 text-sm text-white/80">
              <li className="flex gap-3"><MapPin size={18} weight="bold" className="text-[#fe4c1c] shrink-0 mt-0.5"/>{SITE.address}</li>
              <li className="flex gap-3 items-center"><Phone size={18} weight="bold" className="text-[#fe4c1c] shrink-0"/><a href={`tel:${SITE.phoneRaw}`} className="hover:text-[#fe4c1c]">{SITE.phone}</a></li>
              <li className="flex gap-3 items-center"><EnvelopeSimple size={18} weight="bold" className="text-[#fe4c1c] shrink-0"/><a href={`mailto:${SITE.email}`} className="hover:text-[#fe4c1c] break-all">{SITE.email}</a></li>
            </ul>
          </div>
        </div>

        <div className="pt-8 border-t border-white/15 flex flex-col md:flex-row md:items-center md:justify-between gap-4 text-xs text-white/60">
          <p>© {new Date().getFullYear()} {SITE.name}. All rights reserved.</p>
          <p>Crafted in {SITE.city}, India.</p>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
